/**
 * Google Maps Core API
 * Single source of truth for all Google Maps API interactions
 * Functional approach with modern React patterns
 */

import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_ENDPOINTS, ROUTE_CONFIG, MAP_ERRORS } from './constants';
import {
  transformDirectionsResponse,
  transformGeocodingResponse,
  transformPlacesResponse,
} from './transformer';

/**
 * Fetch route between two points using Google Directions API
 * @param {Object} start - { lat, lng }
 * @param {Object} end - { lat, lng }
 * @param {Object} options - Route options
 * @returns {Promise<Object>} Transformed route data
 */
export async function fetchRoute(start, end, options = {}) {
  if (!GOOGLE_MAPS_API_KEY) {
    throw new Error(MAP_ERRORS.NO_API_KEY);
  }

  if (!start?.lat || !start?.lng || !end?.lat || !end?.lng) {
    throw new Error(MAP_ERRORS.INVALID_COORDINATES);
  }

  const params = new URLSearchParams({
    origin: `${start.lat},${start.lng}`,
    destination: `${end.lat},${end.lng}`,
    key: GOOGLE_MAPS_API_KEY,
    mode: options.travelMode || ROUTE_CONFIG.travelMode,
  });

  // Add optional route preferences
  const avoid = [];
  if (options.avoidTolls ?? ROUTE_CONFIG.avoidTolls) avoid.push('tolls');
  if (options.avoidHighways ?? ROUTE_CONFIG.avoidHighways) avoid.push('highways');
  if (options.avoidFerries ?? ROUTE_CONFIG.avoidFerries) avoid.push('ferries');
  if (avoid.length > 0) {
    params.append('avoid', avoid.join('|'));
  }

  // Add waypoints if provided
  if (options.waypoints && options.waypoints.length > 0) {
    const waypointsStr = options.waypoints.map((wp) => `${wp.lat},${wp.lng}`).join('|');
    params.append('waypoints', waypointsStr);
    if (options.optimizeWaypoints ?? ROUTE_CONFIG.optimizeWaypoints) {
      params.append('optimize', 'true');
    }
  }

  try {
    const response = await fetch(`${GOOGLE_MAPS_ENDPOINTS.directions}?${params.toString()}`);
    const data = await response.json();

    if (data.status !== 'OK') {
      console.error('[GoogleMaps] Directions API error:', data.status, data.error_message);
      throw new Error(data.error_message || MAP_ERRORS.NO_ROUTE_FOUND);
    }

    return transformDirectionsResponse(data);
  } catch (error) {
    console.error('[GoogleMaps] fetchRoute error:', error);
    throw error;
  }
}

/**
 * Geocode an address to coordinates
 * @param {string} address - Address to geocode
 * @param {Object} options - Geocoding options
 * @returns {Promise<Object>} Location data
 */
export async function geocodeAddress(address, options = {}) {
  if (!GOOGLE_MAPS_API_KEY) {
    throw new Error(MAP_ERRORS.NO_API_KEY);
  }

  if (!address) {
    throw new Error('Address is required');
  }

  const params = new URLSearchParams({
    address,
    key: GOOGLE_MAPS_API_KEY,
    ...options,
  });

  try {
    const response = await fetch(`${GOOGLE_MAPS_ENDPOINTS.geocode}?${params.toString()}`);
    const data = await response.json();

    if (data.status !== 'OK') {
      console.error('[GoogleMaps] Geocoding error:', data.status);
      throw new Error(MAP_ERRORS.GEOCODING_FAILED);
    }

    return transformGeocodingResponse(data);
  } catch (error) {
    console.error('[GoogleMaps] geocodeAddress error:', error);
    throw error;
  }
}

/**
 * Reverse geocode coordinates to address
 * @param {Object} location - { lat, lng }
 * @returns {Promise<string>} Formatted address
 */
export async function reverseGeocode(location) {
  if (!GOOGLE_MAPS_API_KEY) {
    throw new Error(MAP_ERRORS.NO_API_KEY);
  }

  if (!location?.lat || !location?.lng) {
    throw new Error(MAP_ERRORS.INVALID_COORDINATES);
  }

  const params = new URLSearchParams({
    latlng: `${location.lat},${location.lng}`,
    key: GOOGLE_MAPS_API_KEY,
  });

  try {
    const response = await fetch(`${GOOGLE_MAPS_ENDPOINTS.geocode}?${params.toString()}`);
    const data = await response.json();

    if (data.status !== 'OK' || !data.results || !data.results[0]) {
      return 'Unknown location';
    }

    return data.results[0].formatted_address;
  } catch (error) {
    console.error('[GoogleMaps] reverseGeocode error:', error);
    return 'Error fetching address';
  }
}

/**
 * Search for places by text query
 * @param {string} query - Search query
 * @param {Object} location - Optional location bias { lat, lng }
 * @param {number} radius - Search radius in meters
 * @returns {Promise<Array>} Array of places
 */
export async function searchPlaces(query, location = null, radius = 5000) {
  if (!GOOGLE_MAPS_API_KEY) {
    throw new Error(MAP_ERRORS.NO_API_KEY);
  }

  const params = new URLSearchParams({
    query,
    key: GOOGLE_MAPS_API_KEY,
  });

  if (location) {
    params.append('location', `${location.lat},${location.lng}`);
    params.append('radius', radius.toString());
  }

  try {
    const response = await fetch(`${GOOGLE_MAPS_ENDPOINTS.placeSearch}?${params.toString()}`);
    const data = await response.json();

    if (data.status !== 'OK') {
      return [];
    }

    return transformPlacesResponse(data);
  } catch (error) {
    console.error('[GoogleMaps] searchPlaces error:', error);
    return [];
  }
}

/**
 * Calculate distance matrix between multiple origins and destinations
 * @param {Array} origins - Array of { lat, lng }
 * @param {Array} destinations - Array of { lat, lng }
 * @param {Object} options - Distance matrix options
 * @returns {Promise<Object>} Distance matrix data
 */
export async function getDistanceMatrix(origins, destinations, options = {}) {
  if (!GOOGLE_MAPS_API_KEY) {
    throw new Error(MAP_ERRORS.NO_API_KEY);
  }

  const originsStr = origins.map((o) => `${o.lat},${o.lng}`).join('|');
  const destinationsStr = destinations.map((d) => `${d.lat},${d.lng}`).join('|');

  const params = new URLSearchParams({
    origins: originsStr,
    destinations: destinationsStr,
    key: GOOGLE_MAPS_API_KEY,
    mode: options.travelMode || ROUTE_CONFIG.travelMode,
  });

  try {
    const response = await fetch(`${GOOGLE_MAPS_ENDPOINTS.distanceMatrix}?${params.toString()}`);
    const data = await response.json();

    if (data.status !== 'OK') {
      throw new Error('Distance matrix calculation failed');
    }

    return data;
  } catch (error) {
    console.error('[GoogleMaps] getDistanceMatrix error:', error);
    throw error;
  }
}
